
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { useMediaQuery, useTheme } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    DatePicker,
    DefaultFooterPosition,
    FormBreadcrumbs,
    GroupedField,
    GroupedFields,
    GroupedRatingTable,
    IGroupedRatingTableRowProps,
    InputOptionMode,
    InstitutionContext,
    MasterGroupContext,
    PageLayout,
    PatientContext,
    RadioGroup,
    RouteLoading,
    ToggleButtonGroup,
    UserType
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FunctionComponent, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ValidateOn } from '@ngt/forms-core';
import { AppSettings } from '../../../AppSettings';
import SourceDocuments from '../../documents/SourceDocuments';
import TranslatedFooter from './templates/TranslatedFooter';
import { TranslatedProForm } from './TranslatedProForm';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IInternationalPersonalityItemPoolFormProps { }

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */

/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */


const ratings: IGroupedRatingTableRowProps[] = [
    {
        propertyPath: 'iFeelBlue'
    },
    {
        propertyPath: 'iDislikeMyself'
    },
    {
        propertyPath: 'iFeelDown'
    },
    {
        propertyPath: 'iHaveMoodSwings'
    },
    {
        propertyPath: 'iPanicEasily'
    },
    {
        propertyPath: 'iIrritateRarely'
    },
    {
        propertyPath: 'iSeldomFeelBlue'
    },
    {
        propertyPath: 'iFeelComfortable'
    },
    {
        propertyPath: 'iNotEasilyBothered'
    },
    {
        propertyPath: 'iAmVeryPleased'
    },
]

const InternationalPersonalityItemPoolForm: FunctionComponent<IInternationalPersonalityItemPoolFormProps> = () => {
    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const isPatient = React.useMemo(() => {
        return user?.type === UserType.Patient;
    }, [user]);

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canUpdatePro, 
            canAdministerPro
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.OpmsProUpdate,
            Permission.OpmsProAdminister
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const showSteps = isPatient || isMobile;

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            <TranslatedProForm
                formTranslationKey={"forms.internationalPersonalityItemPool"}
                validateOn={ValidateOn.onChange}
                useSteps={showSteps}
                readOnly={showSteps ? !canUpdatePro : !canAdministerPro}
                FooterOptions={{
                    content: <TranslatedFooter isPatient={isPatient} />,
                    position: DefaultFooterPosition
                }}
                DialogOptions={AppSettings.proForms.dialogOptions}
                showEventHeading
                debug={AppSettings.debug}
            >
                {
                    !isPatient && (
                        <GroupedFields>
                            <GroupedField
                                propertyPath="dateCompleted"
                                component={DatePicker}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                            <GroupedField
                                LabelProps={{
                                    label: <></>
                                }}
                                formInputOptions={{
                                    labelMode: InputOptionMode.Auto
                                }}
                                propertyPath="ptDidNotComplete"
                                component={Checkbox}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: showSteps ? 12 : 6,
                                        lg: showSteps ? 12 : 5,
                                        xl: showSteps ? 12 : 5
                                    }
                                }}
                            />
                        </GroupedFields>
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iFeelBlue"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iFeelBlue"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iDislikeMyself"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iDislikeMyself"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iFeelDown"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iFeelDown"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iHaveMoodSwings"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iHaveMoodSwings"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iPanicEasily"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iPanicEasily"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iIrritateRarely"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iIrritateRarely"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iSeldomFeelBlue"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iSeldomFeelBlue"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iFeelComfortable"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iFeelComfortable"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iNotEasilyBothered"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iNotEasilyBothered"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    showSteps && (
                        isExtraSmall ?
                            <GroupedField
                                propertyPath="iAmVeryPleased"
                                component={ToggleButtonGroup}
                                orientation="vertical"
                                spacing={3}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            /> :
                            <GroupedField
                                propertyPath="iAmVeryPleased"
                                component={RadioGroup}
                                FieldGridProps={{
                                    size: {
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                        xl: 12
                                    }
                                }}
                            />
                    )
                }
                {
                    !showSteps && (
                        <div>   
                            <GroupedRatingTable 
                                rows={ratings}
                                TableProps={{
                                    sx: {
                                        tableLayout: 'fixed'
                                    }
                                }}
                                LabelTableHeadCellProps={{
                                    sx: {
                                        width: '100%'
                                    }
                                }}
                                ValueTableHeadCellProps={{
                                    sx: {
                                        width: 150
                                    }
                                }}
                            /> 
                        </div>
                    )
                }
                {
                    canAdministerPro && (
                        <SourceDocuments />
                    )
                }
            </TranslatedProForm>
        </PageLayout>
    );
}


/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default InternationalPersonalityItemPoolForm;
